// Capture side of the WebRTC developer diagnostics.
//
// RsWebRTCProvider owns one tap and attaches it to every viewer peer
// connection. The tap is inert until `setEnabled(true)`; WebRTCDebugProvider
// (nested under WebRTCProvider) enables it, subscribes to its events, and owns
// storage and display.
//
// Events delivered to subscribers (all carry `ts`, an ISO-8601 UTC string, and
// `arcSerial`, the selected ARC's serial or null):
//   { kind: "connection", phase: "open" | "close", cameraKey, connectionId, sessionId, ordinal }
//   (ordinal: 1 for a camera's first connection since page load, 2+ for reconnections)
//   { kind: "sdp", cameraKey, connectionId, origin, type, sdp }
//   { kind: "nal", cameraKey, connectionId, nalType, event, size, hex, ... }  (from the worker)
//   { kind: "warning", cameraKey, connectionId, message }
//   { kind: "reconnect", phase: "scheduled" | "start" | "exhausted", cameraKey, attempt, ... }
//   { kind: "stall", cause, cameraKey, connectionId, ... }

const WORKER_URL = `${process.env.PUBLIC_URL || ""}/webrtc/nal-logger-worker.js`;

export const isNalTransformSupported = () =>
    typeof window.RTCRtpScriptTransform === "function" && typeof Worker !== "undefined";

const videoReceivers = (rtc) =>
    rtc.getReceivers().filter((receiver) => receiver.track?.kind === "video");

export const createWebRTCDebugTap = () => {
    const listeners = new Set();
    const peers = new Map();
    let enabled = false;
    let arcSerial = null;
    let worker = null;
    let connectionCounter = 0;
    // Connections per camera since page load, whether or not logging was on.
    const connectionsByCamera = new Map();

    const emit = (event) => {
        const stamped = { ts: new Date().toISOString(), arcSerial, ...event };
        listeners.forEach((listener) => listener(stamped));
    };

    const ensureWorker = () => {
        if (worker) return worker;
        worker = new Worker(WORKER_URL);
        worker.onmessage = (message) => emit(message.data);
        return worker;
    };

    const installTransform = (peer, receiver) => {
        if (!isNalTransformSupported()) return;
        receiver.transform = new window.RTCRtpScriptTransform(ensureWorker(), {
            cameraKey: peer.cameraKey,
            connectionId: peer.connectionId,
        });
    };

    const emitDescriptions = (peer) => {
        const { rtc, cameraKey, connectionId } = peer;
        [
            ["remote", rtc.remoteDescription],
            ["local", rtc.localDescription],
        ].forEach(([origin, description]) => {
            if (!description?.sdp) return;
            emit({ kind: "sdp", cameraKey, connectionId, origin, type: description.type, sdp: description.sdp });
        });
    };

    const startPeer = (peer) => {
        emit({ kind: "connection", phase: "open", cameraKey: peer.cameraKey, connectionId: peer.connectionId, sessionId: peer.sessionId, ordinal: peer.ordinal });
        if (!isNalTransformSupported()) {
            emit({
                kind: "warning",
                cameraKey: peer.cameraKey,
                connectionId: peer.connectionId,
                message: "This browser has no RTCRtpScriptTransform; NAL units are not logged.",
            });
        } else {
            videoReceivers(peer.rtc).forEach((receiver) => installTransform(peer, receiver));
        }
        // A viewer that was already negotiated when logging was switched on.
        if (peer.rtc.signalingState === "stable") emitDescriptions(peer);
    };

    const stopPeer = (peer) => {
        videoReceivers(peer.rtc).forEach((receiver) => {
            receiver.transform = null;
        });
    };

    return {
        isEnabled: () => enabled,

        // Serial of the ARC the viewers belong to; stamped on every event.
        setArcSerial(serial) {
            arcSerial = serial || null;
        },

        setEnabled(next) {
            if (enabled === next) return;
            enabled = next;
            if (enabled) {
                peers.forEach(startPeer);
                return;
            }
            peers.forEach(stopPeer);
            if (worker) {
                worker.terminate();
                worker = null;
            }
        },

        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },

        // Live peers for stats polling: [{ cameraKey, connectionId, rtc }].
        getPeers: () => [...peers.values()],

        // Forward a provider-side lifecycle event (reconnect attempts, stalls).
        report(event) {
            if (enabled) emit(event);
        },

        // Register a viewer's RTCPeerConnection. Returns its id and a detach function.
        attach({ cameraKey, rtc, sessionId }) {
            connectionCounter += 1;
            const connectionId = `${cameraKey}#${connectionCounter}`;
            const ordinal = (connectionsByCamera.get(cameraKey) || 0) + 1;
            connectionsByCamera.set(cameraKey, ordinal);
            const peer = { cameraKey, connectionId, rtc, sessionId: sessionId || null, ordinal };
            peers.set(connectionId, peer);

            const onTrack = (event) => {
                if (enabled && event.track.kind === "video") installTransform(peer, event.receiver);
            };
            const onSignalingState = () => {
                if (enabled && rtc.signalingState === "stable") emitDescriptions(peer);
            };
            rtc.addEventListener("track", onTrack);
            rtc.addEventListener("signalingstatechange", onSignalingState);

            if (enabled) startPeer(peer);

            const detach = () => {
                rtc.removeEventListener("track", onTrack);
                rtc.removeEventListener("signalingstatechange", onSignalingState);
                peers.delete(connectionId);
                if (enabled) {
                    emit({ kind: "connection", phase: "close", cameraKey, connectionId, sessionId: peer.sessionId });
                }
            };
            return { connectionId, detach };
        },
    };
};
